import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "submitBtn",
    "moduleCount",
    "selectedResources",
    "academicYearSelect",
    "fiscalYearSelect",
  ];
  static values = {
    moduleCountsUrl: String,
  };

  connect() {
    this.updateSelection();
    this.updateSelectedResourcesBadges();
  }

  updateSelection() {
    const checkboxes = document.querySelectorAll(
      'input[name="export[modules][]"]:checked',
    );
    const hasSelection = checkboxes.length > 0;
    this.updateSubmitButton(hasSelection);
    this.updateSelectedResourcesBadges();
  }

  async updateCounts(event) {
    // Get both academic year and fiscal year values
    const academicYearId = this.hasAcademicYearSelectTarget
      ? this.academicYearSelectTarget.value
      : null;
    const fiscalYear = this.hasFiscalYearSelectTarget
      ? this.fiscalYearSelectTarget.value
      : null;

    if (!academicYearId || !this.moduleCountsUrlValue) return;

    try {
      let url = `${this.moduleCountsUrlValue}?academic_year_id=${academicYearId}`;
      if (fiscalYear) {
        url += `&fiscal_year=${fiscalYear}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.updateModuleCounts(data.counts);
    } catch (error) {
      console.error("Error fetching module counts:", error);
    }
  }

  updateModuleCounts(counts) {
    // Update each module count badge and enable/disable checkboxes
    Object.entries(counts).forEach(([moduleKey, count]) => {
      const countElement = document.querySelector(
        `[data-module-count="${moduleKey}"]`,
      );
      if (countElement) {
        countElement.textContent = count;
      }

      // Enable/disable checkbox based on count
      const moduleItem = document.querySelector(
        `[data-module-item="${moduleKey}"]`,
      );
      if (moduleItem) {
        const checkbox = moduleItem.querySelector('input[type="checkbox"]');
        if (checkbox) {
          const isDisabled = count === 0;
          checkbox.disabled = isDisabled;

          // Update label styling
          if (isDisabled) {
            moduleItem.classList.add("opacity-50", "cursor-not-allowed");
            moduleItem.classList.remove("cursor-pointer", "hover:border-cta");
            // Uncheck if it was checked
            if (checkbox.checked) {
              checkbox.checked = false;
            }
          } else {
            moduleItem.classList.remove("opacity-50", "cursor-not-allowed");
            moduleItem.classList.add("cursor-pointer", "hover:border-cta");
          }
        }
      }
    });

    // Update badges after count changes (some might need to be removed)
    this.updateSelectedResourcesBadges();
    this.updateSubmitButton(
      document.querySelectorAll('input[name="export[modules][]"]:checked')
        .length > 0,
    );
  }

  updateSelectedResourcesBadges() {
    if (!this.hasSelectedResourcesTarget) return;

    const container = this.selectedResourcesTarget;
    const checkboxes = document.querySelectorAll(
      'input[name="export[modules][]"]:checked',
    );
    const noResourcesMsg = document.getElementById("no-resources-selected");

    // Clear existing badges (but keep the "no resources" message)
    const existingBadges = container.querySelectorAll(".resource-badge");
    existingBadges.forEach((badge) => badge.remove());

    if (checkboxes.length === 0) {
      if (noResourcesMsg) noResourcesMsg.classList.remove("hidden");
    } else {
      if (noResourcesMsg) noResourcesMsg.classList.add("hidden");

      checkboxes.forEach((checkbox) => {
        // The checkbox sits in its own DS <label>; the module name lives on the
        // surrounding module card.
        const moduleItem = checkbox.closest("[data-module-item]");
        const nameElement = moduleItem?.querySelector(".text-sm.font-medium");
        const moduleName = nameElement
          ? nameElement.textContent.trim()
          : checkbox.value;
        const moduleKey = checkbox.value;

        const badge = document.createElement("span");
        badge.className =
          "resource-badge inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-cta-shared text-cta-darker border border-cta";
        badge.dataset.moduleKey = moduleKey;
        badge.innerHTML = `
          <button type="button" class="ml-1 hover:text-cta focus:outline-none" data-action="click->export-form#removeBadge" data-module-key="${moduleKey}">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        `;
        badge.prepend(document.createTextNode(moduleName));
        container.appendChild(badge);
      });
    }
  }

  removeBadge(event) {
    event.preventDefault();
    const moduleKey = event.currentTarget.dataset.moduleKey;

    // Uncheck the corresponding checkbox
    const checkbox = document.querySelector(
      `input[name="export[modules][]"][value="${moduleKey}"]`,
    );
    if (checkbox) {
      checkbox.checked = false;
      this.updateSelection();
    }
  }

  submitForm(event) {
    const checkboxes = document.querySelectorAll(
      'input[name="export[modules][]"]:checked',
    );
    const selectedModules = Array.from(checkboxes).map((cb) => cb.value);

    if (selectedModules.length === 0) {
      event.preventDefault();
      alert("Veuillez sélectionner au moins un module à exporter.");
      return;
    }

    // Don't call this.element.submit() - the form is already submitting naturally
    // Just let the default form submission happen
  }

  updateSubmitButton(hasSelection) {
    const submitBtn = this.submitBtnTarget;
    if (submitBtn) {
      submitBtn.disabled = !hasSelection;
    }
  }
}
